import React from "react"
import { Link as RouterLink } from "react-router-dom"
import { Box, ButtonBase, Tooltip, Typography } from "@mui/material"
import { alpha, keyframes } from "@mui/material/styles"
import { IconSeeker } from "@ui-seeker/core"
import { useApp } from "@app"

import IrisMark, { IRIS } from "@components/brand/IrisMark"
import { userData } from "@signals"
import { irisProfile } from "@signals/irisProfile"
import { USER_ROUTES } from "@constants"
import { dsColorNew } from "@palette"
import { hasEditableProfile } from "@utils/irisProfile"
import { isIrisUser } from "Layout/PmsAgentWidget"
import useIrisState from "Layout/useIrisState"
import { irisLabel, irisSubline } from "Layout/irisState"

/**
 * The launcher of Iris (the PMS agent widget) at the foot of the side menu — variant M+ of
 * `company/missions/CU-869fkdhjv/ux-launcher-placement-note.md` (Curro, 2026-10-21): the
 * mark on a raised navy tile inside the blue → cyan gradient ring, her name and a state
 * line. Expanded menu: a 56 px block the width of the menu with "Iris" and the subline.
 * Mini menu: the 40 px tile alone, centred, with the tooltip saying the state. At both
 * widths the tile sits at the same x, so folding the menu when the drawer opens does not
 * move it.
 *
 * It is mounted outside the menu's scroll area (`BaseDesktopDrawer`'s `footer` slot), so a
 * long menu scrolls under it and the launcher stays pinned to the foot.
 *
 * Only for the users the widget is loaded for (`PmsAgentWidget`'s allowlist), and disabled
 * until the widget says it is mounted (`ready`) — the script is async, so `window.DSB` may
 * not exist yet when the menu paints, or may already exist if it mounted before this did.
 *
 * The states come from `useIrisState` (widget 0.107.0 events): **open** → the tile goes
 * white (the mark becomes line art, as it did on the header); **busy with the drawer
 * closed** → the ring breathes in cyan (a static halo under `prefers-reduced-motion`) and
 * the subline says "Thinking…"; **unread** → the blue dot and "N new answer(s)";
 * **offline** → flat ring, grey mark, red dot and "Not available". The subline reads unread
 * over busy (the note's rule: busy drives the animation, unread the text); offline wins
 * over everything; "Starting…" until the widget is ready.
 *
 * Not here on purpose (the note's "what not to do"): no chevron (it would read as a route
 * with children), no second gradient as a background, no animation at rest, no "IA".
 *
 * Settings › Iris (CU-869fkmt6r; UX D15, Curro 2026-10-21 "the cog always in expanded"): the
 * expanded block holds two sibling targets — the tile opens the drawer; the name, the subline and
 * a 32 px cog go to the Iris page `/user/iris` (CU-869fmherf; in-app navigation, no unsaved-changes
 * guard: leaving Settings by it is like leaving by the side menu). Until
 * the user saves something of theirs (`setup`, the sixth state) the subline reads "Set up Iris"
 * in cyan and the cog is cyan; mini: the tile alone, the tooltip says it and a cog badge marks it.
 */
const toggleIris = () => window.DSB && window.DSB.toggle()

// the widget mounts its drawer inside this root element
const WIDGET_ROOT_ID = 'dsb-root'

// the gradient ring: a flat fill on the padding box, the gradient on the border box — the fill
// must be opaque, or the gradient shows through it
const ring = fill => `linear-gradient(${fill}, ${fill}) padding-box, linear-gradient(135deg, ${dsColorNew.webBlue500}, ${dsColorNew.cyan}) border-box`

// Iris is thinking with the drawer closed: a cyan ripple leaves the ring every 1.6 s
const ripple = keyframes({
	'0%': { boxShadow: `0 0 0 0 ${alpha(dsColorNew.cyan, 0.45)}` },
	'70%': { boxShadow: `0 0 0 8px ${alpha(dsColorNew.cyan, 0)}` },
	'100%': { boxShadow: `0 0 0 0 ${alpha(dsColorNew.cyan, 0)}` },
})

// a new unread dot pops in
const pop = keyframes({
	from: { transform: 'scale(0.4)', opacity: 0 },
	to: { transform: 'scale(1)', opacity: 1 },
})

// the colour and weight of each state line (`irisSubline` decides which one)
const SUBLINE_LOOK = {
	offline: { color: dsColorNew.dangerSoft, weight: 400 },
	starting: { color: alpha(dsColorNew.white, 0.6), weight: 400 },
	unread: { color: dsColorNew.white, weight: 500 },
	busy: { color: dsColorNew.cyan, weight: 400 },
	setup: { color: dsColorNew.cyan, weight: 500 },
	idle: { color: alpha(dsColorNew.white, 0.6), weight: 400 },
}

const SETTINGS_TO = USER_ROUTES.IRIS_PATH

const containerSx = isMini => ({
	mt: 'auto',
	flexShrink: 0,
	px: isMini ? 0 : 1.5,
	pt: 1.5,
	pb: 2,
	borderTop: `1px solid ${alpha(dsColorNew.white, 0.08)}`,
	...(isMini && { display: 'flex', justifyContent: 'center' }),
})

const buttonSx = ({ isMini, open }) => ({
	position: 'relative',
	display: 'flex',
	alignItems: 'center',
	justifyContent: isMini ? 'center' : 'flex-start',
	textAlign: 'left',
	color: dsColorNew.white,
	transition: 'background-color 0.15s ease, border-color 0.15s ease',
	'&.Mui-focusVisible': { outline: `2px solid ${dsColorNew.cyan}`, outlineOffset: 2 },
	'&.Mui-disabled': { opacity: 0.5 },
	...(isMini ? {
		width: 40,
		height: 40,
		p: 0,
		borderRadius: '10px',
		'&:hover .IrisLauncher-tile': { background: ring(dsColorNew.navyRaisedHover) },
	} : {
		width: '100%',
		height: 56,
		pl: '18px',
		pr: '12px',
		gap: '12px',
		borderRadius: '10px',
		backgroundColor: alpha(dsColorNew.white, 0.06),
		border: `1px solid ${alpha(dsColorNew.white, 0.10)}`,
		'&:hover': { backgroundColor: alpha(dsColorNew.white, 0.10), borderColor: alpha(dsColorNew.white, 0.16) },
		...(open && {
			backgroundColor: alpha(dsColorNew.white, 0.12),
			borderColor: alpha(dsColorNew.white, 0.20),
		}),
	}),
})

// expanded: the tile is its own button inside the block (it opens the drawer)
const tileButtonSx = {
	borderRadius: '9px', flexShrink: 0,
	'&.Mui-focusVisible': { outline: `2px solid ${dsColorNew.cyan}`, outlineOffset: 2 },
	'&.Mui-disabled': { opacity: 0.5 },
	'&:hover .IrisLauncher-tile': { background: ring(dsColorNew.navyRaisedHover) },
}

const cogSx = (setup) => ({
	width: 32, height: 32, flexShrink: 0, borderRadius: '8px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
	transition: 'background-color 0.15s ease, border-color 0.15s ease',
	...(setup ? {
		color: dsColorNew.cyan, backgroundColor: alpha(dsColorNew.cyan, 0.12), border: `1px solid ${alpha(dsColorNew.cyan, 0.45)}`,
	} : {
		color: alpha(dsColorNew.white, 0.6), backgroundColor: dsColorNew.navyRaised, border: `1px solid ${alpha(dsColorNew.white, 0.10)}`,
	}),
	'.IrisLauncher-settings:hover &': { color: setup ? dsColorNew.cyan : dsColorNew.white, borderColor: alpha(setup ? dsColorNew.cyan : dsColorNew.white, 0.6) },
})

const tileSx = ({ isMini, open, pulsing, offline }) => ({
	position: 'relative',
	flexShrink: 0,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: isMini ? 40 : 36,
	height: isMini ? 40 : 36,
	borderRadius: isMini ? '10px' : '9px',
	border: '1.5px solid transparent',
	background: ring(dsColorNew.navyRaised),
	transition: 'background 0.15s ease',
	...(open && { background: ring(dsColorNew.white) }),
	...(pulsing && {
		animation: `${ripple} 1.6s ease-out infinite`,
		'@media (prefers-reduced-motion: reduce)': { animation: 'none', boxShadow: `0 0 0 3px ${alpha(dsColorNew.cyan, 0.35)}` },
	}),
	...(offline && {
		background: dsColorNew.navyRaised,
		borderColor: alpha(dsColorNew.white, 0.18),
		'& .IrisLauncher-mark': { filter: 'grayscale(1)', opacity: 0.55 },
	}),
})

// a small round marker pinned to a corner of the tile, ringed in navy so it reads on the tile
const cornerDotSx = (size, color, corner) => ({
	position: 'absolute', ...corner, width: size, height: size, borderRadius: '50%',
	backgroundColor: color, border: `2px solid ${dsColorNew.navy}`, boxSizing: 'content-box',
})

export default function IrisLauncher() {
	const { isMini } = useApp()
	const isAllowed = isIrisUser(userData.value?.username)
	// nothing of the user's saved yet: the sixth state (the identity does not count, criterion 14c)
	const setup = isAllowed && irisProfile.value !== null && !hasEditableProfile(irisProfile.value)
	const state = useIrisState(isAllowed, { setup })
	const { ready, open, busy, unread, offline } = state
	const label = irisLabel(state)
	const line = irisSubline(state)
	const look = SUBLINE_LOOK[line.key] || SUBLINE_LOOK.idle

	if (!isAllowed) return null

	const tile = <Box className="IrisLauncher-tile" sx={tileSx({ isMini, open, pulsing: busy && !open, offline })}>
		<Box className="IrisLauncher-mark" sx={{ display: 'flex' }}><IrisMark size={IRIS.avatar}/></Box>
		{unread > 0 && <Box sx={{ ...cornerDotSx(10, dsColorNew.appBlue, { top: -4, right: -4 }), animation: `${pop} 0.25s ease-out` }}/>}
		{offline && <Box sx={cornerDotSx(9, dsColorNew.dangerRed, { bottom: -3, right: -3 })}/>}
		{isMini && setup && !offline && <Box sx={{
			...cornerDotSx(16, dsColorNew.navyRaised, { bottom: -5, right: -5 }), boxSizing: 'border-box', width: 16, height: 16,
			display: 'flex', alignItems: 'center', justifyContent: 'center', color: dsColorNew.cyan, borderColor: dsColorNew.navy,
		}}><IconSeeker icon="gear" sx={{ fontSize: 9 }}/></Box>}
	</Box>

	if (isMini) return <Box className="IrisLauncher" sx={containerSx(isMini)}>
		<Tooltip title={label} placement="right" arrow><span>
			<ButtonBase
				onClick={toggleIris}
				disabled={!ready}
				aria-label={label}
				aria-expanded={open}
				aria-controls={WIDGET_ROOT_ID}
				sx={buttonSx({ isMini, open })}
			>
				{tile}
			</ButtonBase>
		</span></Tooltip>
	</Box>

	// expanded: the tile, the name and the state line open Iris (Curro, 2026-10-21: "click en medio
	// debería abrirlo"); only the cog goes to Settings › Iris. Two sibling targets, never a link in a button.
	return <Box className="IrisLauncher" sx={containerSx(isMini)}>
		<Box sx={buttonSx({ isMini, open })}>
			<ButtonBase
				onClick={toggleIris}
				disabled={!ready}
				aria-label={irisLabel({ ...state, setup: false })}
				aria-expanded={open}
				aria-controls={WIDGET_ROOT_ID}
				sx={{ ...tileButtonSx, flex: 1, minWidth: 0, height: '100%', justifyContent: 'flex-start', gap: '12px', textAlign: 'left' }}
			>
				{tile}
				<Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: 1.25 }}>
					<Typography component="span" sx={{ fontSize: 14, fontWeight: 600, lineHeight: 'inherit', color: dsColorNew.white, whiteSpace: 'nowrap' }}>Iris</Typography>
					<Typography
						component="span"
						sx={{ fontSize: 12, fontWeight: look.weight, lineHeight: 'inherit', color: look.color, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
					>
						{line.text}
					</Typography>
				</Box>
			</ButtonBase>
			<Tooltip title={setup ? 'Set up Iris — Settings › Iris' : 'Iris settings'} placement="top" arrow>
				<ButtonBase
					component={RouterLink}
					to={SETTINGS_TO}
					className="IrisLauncher-settings"
					aria-label={setup ? 'Set up Iris — Settings › Iris' : 'Iris settings — Settings › Iris'}
					sx={{ borderRadius: '8px', flexShrink: 0, '&.Mui-focusVisible': { outline: `2px solid ${dsColorNew.cyan}`, outlineOffset: 2 } }}
				>
					<Box component="span" aria-hidden="true" sx={cogSx(setup)}><IconSeeker icon="gear" sx={{ fontSize: 13 }}/></Box>
				</ButtonBase>
			</Tooltip>
		</Box>
	</Box>
}
